import type { StructuredResponse, StructuredSection } from './types';

export function sectionHasContent(section: StructuredSection): boolean {
  switch (section.type) {
    case 'table':
      return !!(section.columns?.length && section.rows?.length);
    case 'kv':
      return !!section.rows?.length;
    case 'list':
    case 'badges':
    case 'cards':
      return !!section.items?.length;
    case 'paragraph':
    case 'quote':
      return !!section.content;
    case 'alert':
      return !!section.title;
    case 'flexible':
      return !!(section.data != null || section.items?.length || section.rows?.length || section.content);
    default:
      return !!(section.data != null || section.items?.length || section.rows?.length || section.content);
  }
}

function sectionTextForDuplicateCheck(section: StructuredSection): string {
  switch (section.type) {
    case 'paragraph':
    case 'quote':
      return section.content ?? '';
    case 'list':
    case 'badges':
    case 'cards':
      return (section.items ?? [])
        .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
        .join(' ');
    case 'kv':
    case 'table':
      return (section.rows ?? [])
        .map((row) => Object.values(row).map((v) => String(v ?? '')).join(' '))
        .join(' ');
    default:
      return typeof section.content === 'string' ? section.content : '';
  }
}

function normalizeForDuplicateCheck(text: string): string {
  return text
    // Drop whole markdown heading lines (not just the leading `#`s) - card
    // sections carry heading text as `section.title`, separate from the
    // `content` this compares against, so a raw fallbackText that still has
    // its headings inline must have them stripped the same way to line up.
    .replace(/^#{1,6}\s+.*$/gm, ' ')
    .replace(/[*_`>#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * #131: identity/containment check that stops the plain fallbackText Section-1
 * lead-in from rendering when it is the same narrative already shown inside
 * the structured card's own sections (e.g. a live-streamed full narrative
 * that should have been a short deterministic intro). A genuine short intro
 * ("Below is the chart overview - see the summary below for details.") never
 * textually overlaps with the card's own section content, so this only fires
 * on real duplication.
 */
export function fallbackTextDuplicatesCardContent(
  fallbackText: string | undefined,
  sections: StructuredSection[] | undefined,
): boolean {
  const trimmedFallback = (fallbackText ?? '').trim();
  if (!trimmedFallback || !sections?.length) return false;
  const cardText = sections.map(sectionTextForDuplicateCheck).filter(Boolean).join(' ');
  const normalizedFallback = normalizeForDuplicateCheck(trimmedFallback);
  const normalizedCard = normalizeForDuplicateCheck(cardText);
  if (!normalizedFallback || !normalizedCard) return false;
  return normalizedCard.includes(normalizedFallback);
}

export function hasUsefulStructuredContent(structured: StructuredResponse | null | undefined): boolean {
  if (!structured) return false;
  const rt = structured.response_type;
  if (rt === 'empty' || rt === 'alert') return true;
  const sections = structured.sections;
  if (!Array.isArray(sections) || sections.length === 0) {
    return !!structured.callout;
  }
  return sections.some(sectionHasContent);
}

/** Chart briefing is narrative-first; suppress merged multi-tool cards unless accordion overview overlay. */
export function structuredPayloadForChartBriefingView(
  intentType: string | undefined,
  structured: StructuredResponse | null | undefined,
): StructuredResponse | undefined {
  if (intentType !== 'chart_briefing') return structured ?? undefined;
  if (!structured) return undefined;
  const m = structured.meta ?? {};
  if (m.chart_briefing_presentation === true || m.summary_narrative_card === true) {
    return structured;
  }
  return undefined;
}

type UiWidget = { type: string; data: Record<string, unknown> };

export function chartBriefingNarrativeFromWidget(
  intentType: string | undefined,
  widget: UiWidget,
): string | undefined {
  if (intentType !== 'chart_briefing' || widget.type !== 'W_SUMMARY_CLINICAL') return undefined;
  const summary = String(widget.data?.summary ?? '').trim();
  return summary || undefined;
}

/** Suppress per-tool SSE widgets (tables/titles) for chart briefing; default answers use markdown only. */
export function widgetsForChartBriefingView(
  intentType: string | undefined,
  widgets: UiWidget[] | undefined,
  singleWidget: UiWidget | undefined,
): UiWidget[] {
  if (intentType === 'chart_briefing') return [];
  if (widgets && widgets.length > 0) return widgets;
  if (singleWidget) return [singleWidget];
  return [];
}
